import Leanplum from 'leanplum-sdk';
import { WebpushServiceApi } from './WebpushServiceApi';
import { WebPushServiceWorker } from './WebPushServiceWorker';
import { Url } from '../url';
import { FcmPermissions } from './constants/Permissions';
import { Environment } from "../../../environment/environment";

export class NotificationsWrap {
    public static async isWebPushSubscribed(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const isExistsOnWebpushService = await WebpushServiceApi.getPushSubscriptionState();
            if (!isExistsOnWebpushService) {
                return false;
            }

            Leanplum.setWebPushOptions({
                clientUrl: this.getServiceWorkerUrl()
            });

            return await Leanplum.isWebPushSubscribed();
        } catch (exception) {
            this.logException(exception, 'Leanplum error on getting the subscribe state');
            return false;
        }
    }

    public static async unregisterFromWebPush(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const response = await WebpushServiceApi.setAccountState(false);
            if (!response.isUpdated) {
                return false;
            }

            await Leanplum.unregisterFromWebPush();
            return true;
        } catch (exception) {
            this.logException(exception, 'Leanplum error on unregister from webpush');
            return false;
        }
    }

    public static async subscribeToPushNotifies(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const serviceWorkerRegistration = await WebPushServiceWorker.getSwRegistrations();
            if (serviceWorkerRegistration) {
                const isUnregistered = await serviceWorkerRegistration.unregister();
                if (!isUnregistered) {
                    return false;
                }
            }

            const response = await WebpushServiceApi.setAccountState(true);
            if (!response.isUpdated) {
                return false;
            }

            return await this.registerForLeanplum();
        } catch (exception) {
            this.logException(exception, 'Leanplum registerFoWebPush is fault');
            return false;
        }
    }

    public static async registerForLeanplum(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        Leanplum.setWebPushOptions({
            clientUrl: this.getServiceWorkerUrl()
        });

        return await Leanplum.registerForWebPush(this.getServiceWorkerUrl());
    }

    public static startSession(userId: string): void {
        if(Environment.isSocial()) {
            return;
        }

        Leanplum.setAppIdForProductionMode(
            window.app.config.leanplum.appId,
            window.app.config.leanplum.accessKey
        );

        Leanplum.setWebPushOptions({
            clientUrl: this.getServiceWorkerUrl()
        });

        Leanplum.start(userId);

        Leanplum.addStartResponseHandler((status) => {
            if (!status) {
                console.error('Leanplum addStartResponseHandler is not started');
                return;
            }

            NotificationsWrap.subscribeUserToLeanplumIfExistsInPushService();

            Leanplum.setUserAttributes({
                email: window.app.user.email
            });

            Leanplum.setLocale(this.getLeanplumLocale());

            console.log('Leanplum has started session');
        });
    }

    public static async subscribeUserToLeanplumIfExistsInPushService(): Promise<boolean> {
        if(Environment.isSocial()) {
            return false;
        }

        try {
            const serviceWorkerRegistration = await WebPushServiceWorker.getSwRegistrations();
            if (serviceWorkerRegistration) {
                await WebPushServiceWorker.checkSwUpdates();
            }

            const isAccountExists = await WebpushServiceApi.isAccountExists();
            if (!this.isSupported() && isAccountExists) {
                console.info('Your browser has disabled web notifications');
                return this.unregisterFromWebPush();
            }

            const isSubscribedInLeanplum = await Leanplum.isWebPushSubscribed();
            if (!isAccountExists && isSubscribedInLeanplum) {
                await Leanplum.unregisterFromWebPush();
                return false;
            }

            if (isAccountExists && !isSubscribedInLeanplum) {
                return await this.registerForLeanplum();
            }

            return false;
        } catch (exception) {
            this.logException(exception, 'Leanplum fault on check exists user');
            return false;
        }
    }

    public static isSupported(): boolean {
        if(Environment.isSocial()) {
            return false;
        }

        const pushIsNotLockedState: Array<string> = [
            FcmPermissions.DEFAULT,
            FcmPermissions.GRANTED
        ];
        return Leanplum.isWebPushSupported() && pushIsNotLockedState.includes(Notification.permission);
    }

    private static getServiceWorkerUrl(): string {
        return Url.getNoLangUrl('static/j/firebase-messaging-sw.js');
    }

    private static getLeanplumLocale(): string {
        return window.app?.config?.locale;
    }

    private static logException(exception: Error, message: string): void {
        console.error(message, {
            exception: {
                message: exception.message,
                stack: exception.stack,
                name: exception.name
            }
        });
        window.Sentry.captureException(exception);
    }
}
